/**
 * Rendering REAL-WORLD timestamps in the viewer's chosen time zone.
 *
 * Background (verified 2026-07-28, see
 * docs/superpowers/specs/2026-07-28-user-timezone-opt-in-design.md):
 * every persisted timestamp comes from `dbo.GetDate()`, which is
 * `getdate() AT TIME ZONE 'Eastern Standard Time'` returned as a **naive**
 * datetime, and the API serializes it with no `Z`. So the strings arriving here
 * are Eastern wall-clock with no offset attached. `new Date(s)` would treat them
 * as the *browser's* local time, which is why the site has always shown ET
 * digits to everyone regardless of where they are.
 *
 * The rule (opt-in): **no zone set => Eastern, exactly as before. Zone set =>
 * that zone, labeled with that zone.** Sim dates never come through here — they
 * are a calendar, not a moment, and belong to `fmtSimDate`.
 *
 * IF THE API EVER SENDS AN OFFSET, only `parseSiteTimestamp` needs to change
 * (to `new Date(value)`); every caller and the formatting below stay as-is.
 */

/** The zone every stored timestamp is written in, and the site-wide default. */
export const SITE_TIME_ZONE = 'America/New_York'

/**
 * How far `tz` is from UTC at a given instant, in ms. Derived by formatting the
 * instant *in* the zone and reading the wall clock back, so DST is handled by
 * the platform's tz database rather than by us.
 */
function zoneOffsetMs(instant: Date, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant)

  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? '0')
  // hour can come back as 24 for midnight under hour12:false on some engines.
  const asIfUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour') % 24,
    get('minute'),
    get('second'),
  )
  return asIfUtc - instant.getTime()
}

/**
 * Turn wall-clock components that are known to be in `tz` into a real instant.
 *
 * Two passes: guess the instant by treating the clock reading as UTC, measure
 * the zone's offset there, correct, then re-measure. The second pass matters
 * only near a DST transition, where the offset at the guess differs from the
 * offset at the corrected instant.
 */
function instantFromZonedClock(
  y: number, mo: number, d: number, h: number, mi: number, s: number, tz: string,
): Date {
  const clockAsUtc = Date.UTC(y, mo - 1, d, h, mi, s)
  const firstOffset = zoneOffsetMs(new Date(clockAsUtc), tz)
  let ts = clockAsUtc - firstOffset
  const secondOffset = zoneOffsetMs(new Date(ts), tz)
  if (secondOffset !== firstOffset) ts = clockAsUtc - secondOffset
  return new Date(ts)
}

/**
 * Parse an API timestamp into a real instant, interpreting it as Eastern
 * wall-clock. Accepts the two shapes the API emits:
 *   - ISO-ish  "2026-07-28T15:45:00" (optionally with fractional seconds)
 *   - legacy US "7/28/2026 3:45:00 PM"
 * A value that already carries an offset or `Z` is trusted as an absolute
 * instant and passed through, so this keeps working if the wire contract
 * changes. Returns null for anything unparseable.
 */
export function parseSiteTimestamp(value: string | Date | null | undefined): Date | null {
  if (value == null) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value

  const raw = value.trim()
  if (!raw) return null

  // Already absolute (ends with Z, or carries +hh:mm / -hh:mm after the time).
  if (/(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw)) {
    const d = new Date(raw)
    return Number.isNaN(d.getTime()) ? null : d
  }

  const iso = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/,
  )
  if (iso) {
    return instantFromZonedClock(
      +iso[1], +iso[2], +iso[3], +(iso[4] ?? 0), +(iso[5] ?? 0), +(iso[6] ?? 0),
      SITE_TIME_ZONE,
    )
  }

  const us = raw.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ ,]+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AP]M)?)?/i,
  )
  if (us) {
    let hour = +(us[4] ?? 0)
    const mer = us[7]?.toUpperCase()
    if (mer === 'PM' && hour < 12) hour += 12
    if (mer === 'AM' && hour === 12) hour = 0
    return instantFromZonedClock(
      +us[3], +us[1], +us[2], hour, +(us[5] ?? 0), +(us[6] ?? 0), SITE_TIME_ZONE,
    )
  }

  return null
}

/**
 * The short zone label appended to a rendered time.
 *
 * Prefers the DST-agnostic generic form, so Eastern reads `ET` all year rather
 * than flipping EDT/EST — that's the convention the site and its help are
 * written in. Outside North America the generic form is prose ("France Time"),
 * so it falls back to the offset (`GMT+2`). Getting `CET`/`JST`/`AEST` instead
 * would mean hand-maintaining DST-variant pairs, which silently rots twice a
 * year; an offset is always right.
 */
export function zoneAbbrev(tz: string, at: Date): string {
  if (tz === 'UTC') return 'UTC'
  const name = (style: 'shortGeneric' | 'shortOffset') => {
    try {
      return (
        new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: style })
          .formatToParts(at)
          .find((p) => p.type === 'timeZoneName')?.value ?? ''
      )
    } catch {
      return ''
    }
  }
  const generic = name('shortGeneric')
  if (generic && !generic.includes(' ') && generic.length <= 5) return generic
  return name('shortOffset')
}

/** Resolve a stored preference to the zone we should actually render in. */
export function effectiveZone(tz: string | null | undefined): string {
  if (!tz) return SITE_TIME_ZONE
  try {
    // Reject junk (e.g. the legacy 'America/Central') rather than throwing later.
    new Intl.DateTimeFormat('en-US', { timeZone: tz })
    return tz
  } catch {
    return SITE_TIME_ZONE
  }
}

export interface SiteTimeOptions {
  /** Include the date. Default true. */
  date?: boolean
  /** Include the time of day + zone label. Default true. */
  time?: boolean
  /** Long month/weekday form instead of the compact one. */
  long?: boolean
  /** M/D/YYYY instead of "Jul 28, 2026" — for callers preserving an existing look. */
  numeric?: boolean
  /** "July 28, 2026" — long month, no weekday. */
  monthLong?: boolean
  /** Prefix the short weekday, e.g. "Tue, May 12, 2026". */
  weekday?: boolean
}

/**
 * Format a real-world timestamp for display: converted into `tz` (or Eastern
 * when unset) and suffixed with that zone's label.
 *
 * Returns '' for unparseable input so callers can render their own placeholder.
 */
export function formatSiteTime(
  value: string | Date | null | undefined,
  tz: string | null | undefined,
  opts: SiteTimeOptions = {},
): string {
  const { date = true, time = true, long = false, numeric = false, monthLong = false, weekday = false } = opts
  const instant = parseSiteTimestamp(value)
  if (!instant) return ''

  const zone = effectiveZone(tz)
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    ...(date
      ? long
        ? { weekday: 'long' as const, month: 'long' as const, day: 'numeric' as const, year: 'numeric' as const }
        : numeric
          ? { month: 'numeric' as const, day: 'numeric' as const, year: 'numeric' as const }
          : monthLong
            ? { month: 'long' as const, day: 'numeric' as const, year: 'numeric' as const }
            : {
                ...(weekday ? { weekday: 'short' as const } : {}),
                month: 'short' as const,
                day: 'numeric' as const,
                year: 'numeric' as const,
              }
      : {}),
    ...(time ? { hour: 'numeric' as const, minute: '2-digit' as const } : {}),
  })

  const text = fmt.format(instant)
  return time ? `${text} ${zoneAbbrev(zone, instant)}` : text
}
